import {
  readBoundedAccountJson,
} from "./account-state-session-repository.js";

const ACCESS_PATH = "/api/v1/account/tracker-beta";
const ACCESS_MAX_BYTES = 16 * 1024;
const SHA256 = /^[0-9a-f]{64}$/;
const SERVICE_REGION = /^[A-Z0-9]{1,16}$/;
const BUILD_ID = /^[0-9]{1,32}$/;

function exactFields(value, expected) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const wanted = expected.slice().sort();
  return actual.length === wanted.length
    && actual.every((field, index) => field === wanted[index]);
}

function validDownloadUrl(value) {
  try {
    const url = new URL(value);
    return !url.username
      && !url.password
      && !url.search
      && !url.hash
      && (
        url.protocol === "https:"
        || (url.protocol === "http:" && url.hostname === "127.0.0.1")
      );
  } catch (_error) {
    return false;
  }
}

function boundedNixValue(value, max) {
  return typeof value === "string"
    && value.length > 0
    && value.length <= max
    && !value.startsWith("/")
    && !/[\s`]/u.test(value);
}

export function decodeDesktopBetaAccessV2(value) {
  if (
    !exactFields(value, ["schemaVersion", "release"])
    || value.schemaVersion !== 2
  ) {
    throw new TypeError("Desktop beta access response is invalid.");
  }
  if (exactFields(value.release, ["kind"]) && value.release.kind === "unavailable") {
    return Object.freeze({ release: Object.freeze({ kind: "unavailable" }) });
  }
  const nixOnly = exactFields(value.release, [
    "kind",
    "version",
    "nix",
    "approvedProtocols",
  ]) && value.release.kind === "nix";
  const appImage = exactFields(value.release, [
    "kind",
    "version",
    "appImage",
    "updaterManifestUrl",
    "updaterManifestSha256",
    "nix",
    "approvedProtocols",
  ]) && value.release.kind === "available";
  if (
    (!nixOnly && !appImage)
    || typeof value.release.version !== "string"
    || value.release.version.length === 0
    || value.release.version.length > 64
    || !exactFields(value.release.nix, ["flakeReference", "packageAttribute"])
    || !boundedNixValue(value.release.nix.flakeReference, 512)
    || !boundedNixValue(value.release.nix.packageAttribute, 128)
    || !Array.isArray(value.release.approvedProtocols)
    || value.release.approvedProtocols.length === 0
    || value.release.approvedProtocols.length > 16
    || (appImage && (
      !exactFields(value.release.appImage, ["url", "sha256", "signature"])
      || !validDownloadUrl(value.release.appImage.url)
      || !SHA256.test(value.release.appImage.sha256)
      || typeof value.release.appImage.signature !== "string"
      || value.release.appImage.signature.length === 0
      || value.release.appImage.signature.length > 4096
      || !validDownloadUrl(value.release.updaterManifestUrl)
      || !SHA256.test(value.release.updaterManifestSha256)
    ))
  ) {
    throw new TypeError("Desktop beta release response is invalid.");
  }
  const protocolKeys = new Set();
  const approvedProtocols = value.release.approvedProtocols.map((protocol) => {
    if (
      !exactFields(protocol, ["serviceRegion", "buildId", "releaseSha256"])
      || !SERVICE_REGION.test(protocol.serviceRegion)
      || !BUILD_ID.test(protocol.buildId)
      || !SHA256.test(protocol.releaseSha256)
    ) {
      throw new TypeError("Desktop protocol release is invalid.");
    }
    const key = `${protocol.serviceRegion}/${protocol.buildId}`;
    if (protocolKeys.has(key)) {
      throw new TypeError("Desktop protocol release is duplicated.");
    }
    protocolKeys.add(key);
    return Object.freeze({ ...protocol });
  });
  const release = {
    ...value.release,
    nix: Object.freeze({ ...value.release.nix }),
    approvedProtocols: Object.freeze(approvedProtocols),
  };
  if (appImage) release.appImage = Object.freeze({ ...value.release.appImage });
  return Object.freeze({ release: Object.freeze(release) });
}

function validateAccessUrl(value) {
  let url;
  try {
    url = new URL(value);
  } catch (_error) {
    throw new TypeError("Fishing Logger beta access URL is invalid.");
  }
  if (
    !["http:", "https:"].includes(url.protocol)
    || url.username
    || url.password
    || url.pathname !== ACCESS_PATH
    || url.search
    || url.hash
  ) {
    throw new TypeError("Fishing Logger beta access URL is invalid.");
  }
  return url.href;
}

export async function readTrackerBetaAccessV2({
  url,
  fetchImpl = globalThis.fetch,
  signal,
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("Fishing Logger beta access fetch implementation is unavailable.");
  }
  const response = await fetchImpl(validateAccessUrl(url), {
    method: "GET",
    cache: "no-store",
    credentials: "include",
    headers: { Accept: "application/json" },
    redirect: "error",
    signal,
  });
  if (response.status === 401) {
    return Object.freeze({ kind: "signedOut" });
  }
  if (response.status === 403) {
    return Object.freeze({ kind: "disabled" });
  }
  if (!response.ok) {
    throw new Error("Fishing Logger beta access request failed.");
  }
  return Object.freeze({
    kind: "enabled",
    access: decodeDesktopBetaAccessV2(
      await readBoundedAccountJson(
        response,
        "Fishing Logger beta access",
        ACCESS_MAX_BYTES,
      ),
    ),
  });
}
